import { Step as StepProps } from '@service-victoria/component-library/components/Wizard/props';
import { FormikErrors, FormikTouched } from 'formik';
import * as Interfaces from 'interfaces';
import { constants } from 'utils';
import * as Yup from 'yup';
import { Step as ABN } from '../ABN';
import { Step as Review } from '../Review';
import Component, { validProofTypes } from './Identity';

const RE_EMAIL =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const RE_NUMERIC = /^\d+$/;

// Refer: https://regex101.com/r/dkFASs/6
const RE_PHONE = /^([0]|[+][6][1])4[0-9]{8}$/;

function dateInPast(date: Date): boolean {
  return date.setHours(0, 0, 0, 0) <= new Date().setHours(0, 0, 0, 0);
}

function validDate(value?: string) {
  const [day, month, year] = (value || '').split('/');
  try {
    const parsed = {
      day: parseInt(day, 10),
      month: parseInt(month, 10),
      year: parseInt(year, 10)
    };
    return (
      parsed.day <= 31 &&
      parsed.month <= 12 &&
      parsed.year >= 1000 &&
      // note that month is zero indexed, for the date function, hence -1.
      dateInPast(new Date(parsed.year, parsed.month - 1, parsed.day))
    );
  } catch (e) {
    return false;
  }
}

function isNumericString(value: string) {
  return !!(value && RE_NUMERIC.test(value));
}

type Values = Pick<Interfaces.Common.FormValues, 'identity'>;

const initialValues: Values = {
  identity: {
    confirmEmail: '',
    email: '',
    mobileNumber: '',
    givenName: '',
    familyName: '',
    dob: '',
    cardNo: ''
  }
};
const initialErrors: FormikErrors<Values> = {};
const initialTouched: FormikTouched<Values> = {};

const wizardSchema = Yup.object().shape<Interfaces.Common.Identity>({
  email: Yup.string()
    .required(constants.GenericValidationErrors.required)
    .test(
      'isValidEmail',
      'That doesn’t look like a valid email. Please try again.',
      function validate(email: string) {
        return RE_EMAIL.test(email);
      }
    ),
  confirmEmail: Yup.string()
    .required(constants.GenericValidationErrors.required)
    .test(
      'isValidEmail',
      'That doesn’t look like a valid email. Please try again.',
      function validate(email: string) {
        return RE_EMAIL.test(email);
      }
    )
    .test(
      'isEqual',
      'Sorry, that didn’t match. Please try again.',
      function validate(value: string) {
        const parent: Interfaces.Common.Identity = this.parent;
        return parent.email === value;
      }
    ),
  mobileNumber: Yup.string()
    .required(constants.GenericValidationErrors.required)
    .test(
      'isValidPhone',
      'That doesn’t look like a valid mobile phone number. Please try again.',
      function validate(phone: string) {
        return RE_PHONE.test((phone || '').replace(/\s/g, ''));
      }
    ),
  proofType: Yup.mixed()
    .required(constants.GenericValidationErrors.required)
    .oneOf(
      validProofTypes.map(({ value }) => value),
      constants.GenericValidationErrors.required
    ),
  givenName: Yup.string().required(constants.GenericValidationErrors.required),
  middleName: Yup.string().notRequired(),
  familyName: Yup.string().required(constants.GenericValidationErrors.required),
  dob: Yup.string().test('validDate', constants.GenericValidationErrors.validDate, validDate),
  cardNo: Yup.string().when('proofType', proofType => {
    let lengthValidation;
    if (proofType === 'driver_license')
      lengthValidation = Yup.string().length(9, constants.GenericValidationErrors.validLicense);
    else if (proofType === 'seniorsCard')
      lengthValidation = Yup.string().max(
        10,
        constants.GenericValidationErrors.seniorsCardMaxDigits
      );
    // proofType === 'proofOfAgeCard'
    else
      lengthValidation = Yup.string().max(
        10,
        constants.GenericValidationErrors.proofOfAgeCardMaxDigits
      );
    return lengthValidation
      .required(constants.GenericValidationErrors.required)
      .test('numericString', constants.GenericValidationErrors.isNumeric, isNumericString);
  })
});

const validationSchema = Yup.object().shape<Values>({
  identity: wizardSchema
});

const Step: StepProps<Values> = {
  id: 'details',
  title: constants.Page.title,
  stepName: 'Personal details',
  validation: {
    schema: validationSchema,
    fieldNames: () => [
      'identity.email',
      'identity.confirmEmail',
      'identity.proofType',
      'identity.mobileNumber',
      'identity.dob',
      'identity.cardNo',
      'identity.givenName',
      'identity.familyName'
    ]
  },
  Component,
  navigation: {
    normal: [
      {
        content: 'Back',
        type: 'secondary',
        shouldValidate: false,
        onClick: (wizardContext, formProps) => {
          wizardContext.dispatch({
            type: 'GOTO_STEP',
            stepId: ABN.id,
            values: formProps.values
          });
        }
      },
      {
        content: 'Next',
        shouldValidate: true,
        type: 'primary',
        onClick: (context, formProps) => {
          context.dispatch({
            type: 'GOTO_STEP',
            stepId: Review.id,
            values: formProps.values
          });
        }
      }
    ]
  }
};

export { Step, initialErrors, wizardSchema, initialTouched, initialValues };
